import { useEffect, useRef, useState } from 'react';

/** Live-search debounce (DESIGN.md rule 2: about 200 ms). Pass a longer delay only with a recorded reason. */
export const SEARCH_DEBOUNCE_MS = 200;

export function useDebouncedValue<T>(value: T, delayMs = SEARCH_DEBOUNCE_MS): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Points a media element at a Blob via an object URL, revoked on change/unmount. Sets the DOM
 * property directly (no state), so StrictMode's double effects never leave a revoked URL behind.
 */
export function useBlobSrc<E extends HTMLMediaElement | HTMLImageElement>(blob: Blob | undefined) {
  const ref = useRef<E>(null);
  useEffect(() => {
    const el = ref.current;
    if (!blob || !el) return undefined;
    const url = URL.createObjectURL(blob);
    el.src = url;
    return () => {
      el.removeAttribute('src');
      URL.revokeObjectURL(url);
    };
  }, [blob]);
  return ref;
}
